import { useState, type FormEvent, type ReactNode } from "react";
import type {
  PortalId,
  YourSignInData,
} from "@shared-core/domain/portal-sign-in/types";
import { Button } from "../primitives/button";
import { Card } from "../primitives/card";
import { Input } from "../primitives/input";
import { Label } from "../primitives/label";
import { cn } from "../primitives/utils";
import { GoogleLogo, MicrosoftLogo } from "./ProviderLogos";

export interface YourSignInSectionProps {
  data: YourSignInData;
  /** The portal this page is in, so a passkey of the other one is named. */
  thisPortal: PortalId;
  /** How each portal is named in a sentence: "customer portal", "supplier portal". */
  portalNames: Record<PortalId, string>;
  /** This device can use a passkey. */
  passkeysAvailable: boolean;
  busy?: boolean;
  onSaveLabel(label: string): void;
  onAddPasskey(): void;
  onRemovePasskey(passkeyId: string): void;
  onSignOutEverywhere(): void;
}

const PROVIDERS = {
  microsoft: { name: "Microsoft", Logo: MicrosoftLogo },
  google: { name: "Google", Logo: GoogleLogo },
} as const;

/** A card's title: the sentence that names what the card shows. */
function CardTitle({ children }: { children: string }) {
  return <h3 className="text-sm font-semibold">{children}</h3>;
}

/** A status at a row's right edge: green when on or linked. */
function Pill({ children, ok = false }: { children: string; ok?: boolean }) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-px text-[11.5px] font-semibold whitespace-nowrap",
        ok ? "bg-green-50 text-green-700" : "bg-muted text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

/** One line of a card: a title over its detail, with a status or a button at the right. Lines are divided. */
function Row({
  title,
  detail,
  mark,
  trailing,
}: {
  title?: string;
  detail: string;
  mark?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2.5 border-t py-[11px]">
      <div className="flex min-w-0 flex-col gap-px">
        {title ? (
          <span className="flex items-center gap-2 text-sm font-semibold">
            {mark}
            {title}
          </span>
        ) : null}
        <span className="text-[12.5px] text-muted-foreground">{detail}</span>
      </div>
      {trailing}
    </div>
  );
}

const rowsWithoutFirstDivider = "[&>*:first-child]:border-t-0";

/**
 * "Your sign-in" on the Account page of both portals: a section heading, then three cards: the name shown for the
 * address, how the person signs in (the passkeys, each with its portal), and where the address is signed in with
 * *Sign out everywhere*.
 */
export function YourSignInSection({
  data,
  thisPortal,
  portalNames,
  passkeysAvailable,
  busy = false,
  onSaveLabel,
  onAddPasskey,
  onRemovePasskey,
  onSignOutEverywhere,
}: YourSignInSectionProps) {
  const [label, setLabel] = useState(data.label ?? "");
  const labelChanged = label.trim() !== (data.label ?? "");

  // There is no Save button: the name is kept when the person leaves the field or presses Enter.
  function saveLabel(event?: FormEvent) {
    event?.preventDefault();
    if (labelChanged) onSaveLabel(label.trim());
  }

  return (
    <section className="space-y-3.5">
      <h2 className="text-[15px] font-semibold">Your sign-in</h2>

      <Card className="gap-3 p-5">
        <form className="space-y-3" onSubmit={saveLabel}>
          <Label
            htmlFor="portal-your-sign-in-label"
            className="text-[13.5px] font-medium"
          >
            Name for this address
          </Label>
          <Input
            id="portal-your-sign-in-label"
            className="h-10 rounded-lg"
            placeholder="e.g. Orders team, or your name"
            value={label}
            maxLength={80}
            disabled={busy}
            onChange={(event) => setLabel(event.target.value)}
            onBlur={() => saveLabel()}
          />
          <p className="text-[13px] text-muted-foreground">{data.labelHelp}</p>
        </form>
      </Card>

      <Card className="gap-3 p-5">
        <CardTitle>{`How ${data.address} signs in`}</CardTitle>
        <div className={rowsWithoutFirstDivider}>
          {data.linkedAccounts.map((account) => {
            const { name, Logo } = PROVIDERS[account.provider];
            return (
              <Row
                key={account.provider}
                title={name}
                mark={<Logo className="size-[18px]" />}
                detail={`${account.address} · last used ${account.lastUsed}`}
                trailing={<Pill ok>Linked</Pill>}
              />
            );
          })}
          {data.codes ? (
            <Row
              title="Emailed codes"
              detail={`Sent to ${data.address}`}
              trailing={
                <Pill ok={data.codes.on}>{data.codes.on ? "On" : "Off"}</Pill>
              }
            />
          ) : null}
          {data.passkeys.length > 0 ? (
            data.passkeys.map((passkey, index) => (
              <Row
                key={passkey.id}
                title={index === 0 ? "Passkeys" : undefined}
                detail={`${
                  passkey.portal === thisPortal
                    ? passkey.deviceName
                    : `${passkey.deviceName} (${portalNames[passkey.portal]})`
                } · added ${passkey.addedOn} · last used ${passkey.lastUsed}`}
                trailing={
                  <Button
                    variant="outline"
                    className="h-10 px-3 font-medium"
                    disabled={busy}
                    onClick={() => onRemovePasskey(passkey.id)}
                  >
                    Remove
                  </Button>
                }
              />
            ))
          ) : (
            <Row
              title="Passkeys"
              detail="None yet"
              trailing={
                passkeysAvailable ? (
                  <Button
                    variant="outline"
                    className="h-10 px-3 font-medium"
                    disabled={busy}
                    onClick={onAddPasskey}
                  >
                    Add a passkey
                  </Button>
                ) : undefined
              }
            />
          )}
        </div>
      </Card>

      <Card className="gap-3 p-5">
        <CardTitle>{`Where ${data.address} is signed in`}</CardTitle>
        <div>
          {data.sessions.map((session) => (
            <Row
              key={session.id}
              title={session.device}
              detail={
                session.isThisBrowser
                  ? `This browser · ${session.lastUsed}`
                  : session.lastUsed
              }
            />
          ))}
        </div>
        <Button
          variant="outline"
          className="h-10 w-full font-medium"
          disabled={busy}
          onClick={onSignOutEverywhere}
        >
          Sign out everywhere
        </Button>
      </Card>
    </section>
  );
}
